import { Injectable } from "@angular/core";

interface CachedImage {
  name: string;
  dataUrl: string;
  timestamp: number;
}

@Injectable({
  providedIn: "root",
})
export class IndexedDBCacheService {
  private DB_NAME = "MapixImageCache";
  private STORE_NAME = "images";
  private DB_VERSION = 1;
  private db: IDBDatabase = null;

  constructor() {
    this.initDB();
  }

  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error("Error en IndexedDB:", request.error);
        reject(request.error);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: "name" });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
    });
  }

  async saveImage(name: string, dataUrl: string): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);

      const cachedImage: CachedImage = {
        name,
        dataUrl,
        timestamp: Date.now(),
      };

      const request = store.put(cachedImage);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error("Error guardando imagen en IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }

  async getImage(name: string): Promise<string | null> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readonly");
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.get(name);

      request.onsuccess = () => {
        const result = request.result as CachedImage | undefined;
        resolve(result?.dataUrl || null);
      };

      request.onerror = () => {
        console.error("Error trayendo imagen del IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }

  async hasImage(name: string): Promise<boolean> {
    const image = await this.getImage(name);
    return image !== null;
  }

  async getAllImages(): Promise<{ [name: string]: string }> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readonly");
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const result: { [name: string]: string } = {};
        (request.result as CachedImage[]).forEach((item) => {
          result[item.name] = item.dataUrl;
        });
        resolve(result);
      };

      request.onerror = () => {
        console.error("Error trayendo todas las imágenes del IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }

  async getImageCount(): Promise<number> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readonly");
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearCache(): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error("Error limpiando la caché de IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }

  async deleteImage(name: string): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.STORE_NAME, "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.delete(name);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error("Error eliminando imagen del IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }
}
